// ManageMembers.js
import React, { useState, useEffect } from 'react';
import {
  getAllUsers,
  updateUserProfile,
  deleteUser,
  updateUserRole,
  updateUserPoints,
  searchUsers,
  generateMissingMembershipIDs
} from '../../api/users';
import Button from '../../components/ui/Button';
import './ManageEvents.css'; // Reusing the same styles

const ManageMembers = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const toMillis = (value) => {
    if (!value) return 0;
    try {
      if (value.toDate) return value.toDate().getTime();
      const date = new Date(value);
      const ms = date.getTime();
      return Number.isFinite(ms) ? ms : 0;
    } catch {
      return 0;
    }
  };

  const formatCreatedAt = (createdAt) => {
    const ms = toMillis(createdAt);
    if (!ms) return 'N/A';
    return new Date(ms).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format last login timestamp
  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';

    try {
      // Handle Firestore Timestamp object
      const date = lastLogin.toDate ? lastLogin.toDate() : new Date(lastLogin);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

      // For older dates, show the actual date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      console.error('Error formatting last login:', error);
      return 'Unknown';
    }
  };
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'member',
    points: 0,
    membershipID: '',
    specialty: '',
    workplace: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [generatingIDs, setGeneratingIDs] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, searchTerm, filterRole, sortConfig]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const membersData = await getAllUsers();
      const normalizedMembers = (membersData || []).map((member) => ({
        ...member,
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        role: member.role || 'member',
        points: typeof member.points === 'number' ? member.points : 0,
        isActive: member.isActive !== undefined ? member.isActive : true
      }));
      setMembers(normalizedMembers);
      setError(null);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Failed to load members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterMembers = () => {
    let filtered = [...members];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(member => {
        const fullName = `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase();
        const email = (member.email || '').toLowerCase();
        const membershipID = (member.membershipID || '').toLowerCase();
        return fullName.includes(search) || email.includes(search) || membershipID.includes(search);
      });
    }

    // Filter by role
    if (filterRole !== 'all') {
      filtered = filtered.filter(member => member.role === filterRole);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special cases
      if (sortConfig.key === 'name') {
        aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
        bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
      } else if (sortConfig.key === 'lastLogin') {
        aValue = aValue ? (aValue.toDate ? aValue.toDate().getTime() : new Date(aValue).getTime()) : 0;
        bValue = bValue ? (bValue.toDate ? bValue.toDate().getTime() : new Date(bValue).getTime()) : 0;
      } else if (sortConfig.key === 'createdAt') {
        aValue = toMillis(aValue);
        bValue = toMillis(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredMembers(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const toggleExpandRow = (memberId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(memberId)) {
      newExpanded.delete(memberId);
    } else {
      newExpanded.add(memberId);
    }
    setExpandedRows(newExpanded);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? <span className="sort-icon">↑</span> : <span className="sort-icon">↓</span>;
  };

  const exportToCSV = () => {
    try {
      // Define CSV headers
      const headers = [
        'Membership ID',
        'Title',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Role',
        'Specialty',
        'Workplace',
        'Designation',
        'Country',
        'Points',
        'Email Verified',
        'Status',
        'Member Since',
        'Last Login'
      ];

      // Convert data to CSV rows
      const rows = filteredMembers.map(member => [
        member.membershipID || '',
        member.title || '',
        member.firstName || '',
        member.lastName || '',
        member.email || '',
        member.phone || '',
        member.role || 'member',
        member.specialty || '',
        member.workplace || '',
        member.designation || '',
        member.country || '',
        member.points || 0,
        member.emailVerified ? 'Yes' : 'No',
        member.isActive ? 'Active' : 'Inactive',
        member.createdAt ? new Date(toMillis(member.createdAt)).toLocaleDateString() : '',
        member.lastLogin ? formatLastLogin(member.lastLogin) : 'Never'
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row.map(cell => {
            // Escape quotes and wrap in quotes if contains comma
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
        )
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `KOS_Members_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`Exported ${filteredMembers.length} members to CSV`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      setError('Failed to export data. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'member',
      points: member.points || 0,
      membershipID: member.membershipID || '',
      specialty: member.specialty || '',
      workplace: member.workplace || ''
    });
    setShowEditForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to delete ${memberName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setSubmitting(true);
      await deleteUser(memberId);
      setMembers(members.filter(m => m.id !== memberId));
      setSuccess('Member deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting member:', err);
      setError('Failed to delete member. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await updateUserProfile(editingMember.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        role: formData.role,
        points: formData.points,
        membershipID: formData.membershipID,
        specialty: formData.specialty,
        workplace: formData.workplace
      });

      // Update local state
      setMembers(members.map(m =>
        m.id === editingMember.id
          ? { ...m, ...formData }
          : m
      ));

      setSuccess('Member updated successfully!');
      setTimeout(() => {
        setSuccess(null);
        setShowEditForm(false);
        setEditingMember(null);
      }, 2000);
    } catch (err) {
      console.error('Error updating member:', err);
      setError('Failed to update member. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingMember(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'member',
      points: 0,
      membershipID: '',
      specialty: '',
      workplace: ''
    });
    setError(null);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'role-badge role-admin';
      case 'volunteer':
        return 'role-badge role-volunteer';
      default:
        return 'role-badge role-member';
    }
  };

  const handleGenerateMembershipIDs = async () => {
    if (!window.confirm('This will generate membership IDs for all users who don\'t have one. Continue?')) {
      return;
    }

    setGeneratingIDs(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await generateMissingMembershipIDs();
      setSuccess(`${result.message}. Refreshing members list...`);

      // Refresh the members list
      setTimeout(async () => {
        await fetchMembers();
        setSuccess(null);
      }, 2000);
    } catch (err) {
      console.error('Error generating membership IDs:', err);
      setError('Failed to generate membership IDs. Please try again.');
    } finally {
      setGeneratingIDs(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-events">
        <div className="loading-spinner">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="manage-events manage-members">
      <div className="manage-events-header">
        <h1>Manage Members</h1>
      </div>

      {/* Alert Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filters and Search */}
      <div className="event-form-container" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <input
              type="text"
              placeholder="Search by name, email, or membership ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>
          <div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                minWidth: '150px'
              }}
            >
              <option value="all">All Roles</option>
              <option value="member">Members</option>
              <option value="volunteer">Volunteers</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          <div>
            <Button onClick={exportToCSV} variant="secondary">
              📥 Export to CSV
            </Button>
          </div>
          <div>
            <Button
              onClick={handleGenerateMembershipIDs}
              variant="primary"
              disabled={generatingIDs}
            >
              {generatingIDs ? '🔄 Generating...' : '🆔 Generate Missing IDs'}
            </Button>
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>
            Showing {filteredMembers.length} of {members.length} members
          </div>
        </div>
      </div>

      {/* Edit Member Form */}
      {showEditForm && (
        <div className="event-form-container">
          <div className="event-form-header">
            <h2>Edit Member</h2>
            <button className="close-btn" onClick={handleCancelEdit}>&times;</button>
          </div>

          <form onSubmit={handleSubmit} className="event-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="membershipID">Membership ID</label>
                <input
                  type="text"
                  id="membershipID"
                  name="membershipID"
                  value={formData.membershipID}
                  onChange={handleInputChange}
                  placeholder="Enter membership ID"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="member">Member</option>
                  <option value="volunteer">Volunteer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="specialty">Specialty</label>
                <input
                  type="text"
                  id="specialty"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="workplace">Workplace</label>
              <input
                type="text"
                id="workplace"
                name="workplace"
                value={formData.workplace}
                onChange={handleInputChange}
                placeholder="Enter workplace"
              />
            </div>

            <div className="form-group">
              <label htmlFor="points">Points</label>
              <input
                type="number"
                id="points"
                name="points"
                value={formData.points}
                onChange={handleInputChange}
                min="0"
              />
            </div>

            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Members Table */}
      <div className="events-list">
        {filteredMembers.length === 0 ? (
          <div className="no-events">
            <p>No members found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="members-table-redesign">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th onClick={() => handleSort('membershipID')} className="sortable">
                    Member ID {getSortIcon('membershipID')}
                  </th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Name {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    Email {getSortIcon('email')}
                  </th>
                  <th onClick={() => handleSort('role')} className="sortable">
                    Role {getSortIcon('role')}
                  </th>
                  <th onClick={() => handleSort('points')} className="sortable">
                    Points {getSortIcon('points')}
                  </th>
                  <th onClick={() => handleSort('createdAt')} className="sortable">
                    Member Since {getSortIcon('createdAt')}
                  </th>
                  <th onClick={() => handleSort('lastLogin')} className="sortable">
                    Last Login {getSortIcon('lastLogin')}
                  </th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <React.Fragment key={member.id}>
                    <tr className="member-row">
                      <td>
                        <button
                          className="expand-btn"
                          onClick={() => toggleExpandRow(member.id)}
                          title={expandedRows.has(member.id) ? 'Collapse' : 'Expand'}
                        >
                          {expandedRows.has(member.id) ? '▼' : '▶'}
                        </button>
                      </td>
                      <td>
                        <span className="member-id">{member.membershipID || 'N/A'}</span>
                      </td>
                      <td>
                        <div className="member-name">
                          <strong>{member.firstName} {member.lastName}</strong>
                        </div>
                      </td>
                      <td>
                        <span className="member-email">{member.email}</span>
                      </td>
                      <td>
                        <span className={getRoleBadgeClass(member.role)}>
                          {member.role || 'member'}
                        </span>
                      </td>
                      <td>
                        <span className="points-badge">{member.points || 0}</span>
                      </td>
                      <td>
                        <span style={{
                          color: '#666',
                          fontSize: '0.9rem'
                        }}>
                          {formatCreatedAt(member.createdAt)}
                        </span>
                      </td>
                      <td>
                        <span className="last-login-text" style={{
                          color: member.lastLogin ? '#666' : '#999',
                          fontStyle: !member.lastLogin ? 'italic' : 'normal'
                        }}>
                          {formatLastLogin(member.lastLogin)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(member)}
                            title="Edit member"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(member.id, `${member.firstName} ${member.lastName}`)}
                            title="Delete member"
                            disabled={submitting}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows.has(member.id) && (
                      <tr className="expanded-row">
                        <td colSpan="9">
                          <div className="member-details">
                            <div className="details-grid">
                              <div className="detail-item">
                                <label>Phone:</label>
                                <span>{member.phone || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Specialty:</label>
                                <span>{member.specialty || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Workplace:</label>
                                <span>{member.workplace || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Title:</label>
                                <span>{member.title || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Designation:</label>
                                <span>{member.designation || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Country:</label>
                                <span>{member.country || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Member Since:</label>
                                <span>
                                  {formatCreatedAt(member.createdAt)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <label>Email Verified:</label>
                                <span className={member.emailVerified ? 'verified' : 'not-verified'}>
                                  {member.emailVerified ? '✓ Verified' : '✗ Not Verified'}
                                </span>
                              </div>
                              <div className="detail-item">
                                <label>Status:</label>
                                <span className={member.isActive ? 'status-active' : 'status-inactive'}>
                                  {member.isActive ? '● Active' : '● Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageMembers;
